import React, { useState, useEffect } from 'react';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';
import api from '../services/api';

const ProfilePage = () => {
  const [activeTab, setActiveTab] = useState('genel');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [userData, setUserData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    department: '',
    position: '',
    startDate: '',
    birthDate: '',
    address: '',
    emergencyContact: ''
  });

  // Kullanıcı bilgilerini çek
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const response = await api.getProfile();
        if (response.success) {
          setUserData({
            firstName: response.user.firstName || '',
            lastName: response.user.lastName || '',
            email: response.user.email || '',
            phone: response.user.phone || '',
            department: response.user.department || '',
            position: response.user.position || '',
            startDate: response.user.startDate || '',
            birthDate: response.user.birthDate || '',
            address: response.user.address || '',
            emergencyContact: response.user.emergencyContact || '',
            avatar: response.user.avatar || null
          });
        }
      } catch (error) {
        console.error('Kullanıcı bilgileri alınamadı:', error);
        showError('Kullanıcı bilgileri yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [showError]);

  // Tarih formatlama fonksiyonu
  const formatDate = (dateString) => {
    if (!dateString) return 'Belirtilmemiş';

    const date = new Date(dateString);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    return date.toLocaleDateString('tr-TR', options);
  };

  // Çalışma süresi hesaplama fonksiyonu
  const calculateWorkDuration = (startDate) => {
    if (!startDate) return 'Hesaplanamadı';

    const start = new Date(startDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);

    if (years > 0) {
      return `${years} yıl ${months} ay`;
    } else {
      return `${months} ay`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Telefon numarası için mask uygula
    if (name === 'phone') {
      const maskedValue = applyPhoneMask(value);
      setUserData(prev => ({
        ...prev,
        [name]: maskedValue
      }));
    } else {
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Telefon numarası mask fonksiyonu
  const applyPhoneMask = (value) => {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');

    // Telefon numarası formatı: (555) 555 55 55
    if (numbers.length === 0) return '';
    if (numbers.length <= 3) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    if (numbers.length <= 8) return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8)}`;

    // Maksimum 10 rakam
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6, 8)} ${numbers.slice(8, 10)}`;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.updateProfile({
        firstName: userData.firstName,
        lastName: userData.lastName,
        phone: userData.phone,
        address: userData.address,
        emergencyContact: userData.emergencyContact,
        birthDate: userData.birthDate
      });

      if (response.success) {
        showSuccess('Profil bilgileri başarıyla güncellendi!');
        setEditMode(false);
      } else {
        showError(response.message || 'Güncelleme başarısız.');
      }
    } catch (error) {
      console.error('Profil güncelleme hatası:', error);
      showError('Profil güncellenirken hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (passwordData) => {
    try {
      setSaving(true);
      const response = await api.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );

      if (response.success) {
        showSuccess('Şifre başarıyla değiştirildi!');
        // Form'u temizle
        document.getElementById('currentPassword').value = '';
        document.getElementById('newPassword').value = '';
        document.getElementById('confirmPassword').value = '';
      } else {
        showError(response.message || 'Şifre değiştirme başarısız.');
      }
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      showError('Şifre değiştirilirken hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  // Avatar yükleme fonksiyonu
  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Dosya tipi kontrolü
    if (!file.type.startsWith('image/')) {
      showError('Sadece resim dosyaları yüklenebilir.');
      return;
    }

    // Dosya boyutu kontrolü (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Dosya boyutu 5MB\'dan küçük olmalıdır.');
      return;
    }

    try {
      setUploadingAvatar(true);
      const response = await api.uploadAvatar(file);

      if (response.success) {
        showSuccess('Profil fotoğrafı başarıyla yüklendi!');
        // Kullanıcı bilgilerini yeniden yükle
        const profileResponse = await api.getProfile();
        if (profileResponse.success) {
          setUserData(prev => ({
            ...prev,
            avatar: profileResponse.user.avatar
          }));
        }
      } else {
        showError(response.message || 'Avatar yüklenemedi.');
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      showError('Avatar yüklenirken hata oluştu.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Avatar silme fonksiyonu
  const handleAvatarDelete = async () => {
    if (!window.confirm('Profil fotoğrafını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      setUploadingAvatar(true);
      const response = await api.deleteAvatar();

      if (response.success) {
        showSuccess('Profil fotoğrafı başarıyla silindi!');
        // Kullanıcı bilgilerini yeniden yükle
        const profileResponse = await api.getProfile();
        if (profileResponse.success) {
          setUserData(prev => ({
            ...prev,
            avatar: null
          }));
        }
      } else {
        showError(response.message || 'Avatar silinemedi.');
      }
    } catch (error) {
      console.error('Avatar delete error:', error);
      showError('Avatar silinirken hata oluştu.');
    } finally {
      setUploadingAvatar(false);
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="modern-loading">
              <div className="text-center">
                <div className="modern-spinner spinner-border" role="status">
                  <span className="visually-hidden">Yükleniyor...</span>
                </div>
                <h5 className="text-muted mb-3 mt-3">Profil bilgileri yükleniyor...</h5>
                <p className="text-muted">Lütfen bekleyin</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Modern Profile Header with Card Layout */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="modern-card p-5" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            border: '1px solid #e3f2fd',
            boxShadow: '0 8px 32px rgba(144, 202, 249, 0.15)'
          }}>
            <div className="row align-items-center">
              <div className="col-md-4 text-center">
                <div className="position-relative d-inline-block">
                  {userData.avatar && userData.avatar.filePath ? (
                    <img
                      src={`${apiUrl}/uploads/avatars/${userData.avatar.filename}`}
                      alt="Profil Fotoğrafı"
                      className="modern-avatar mx-auto"
                      style={{
                        objectFit: 'cover',
                        border: '4px solid #ffffff',
                        boxShadow: '0 8px 25px rgba(168, 216, 234, 0.4)'
                      }}
                    />
                  ) : (
                    <div
                      className="modern-avatar d-flex align-items-center justify-content-center text-white fw-bold mx-auto"
                      style={{
                        background: 'linear-gradient(135deg, #90caf9 0%, #bbdefb 100%)',
                        fontSize: '48px',
                        border: '4px solid #ffffff',
                        boxShadow: '0 8px 25px rgba(144, 202, 249, 0.4)'
                      }}
                    >
                      {userData.firstName?.charAt(0)}{userData.lastName?.charAt(0)}
                    </div>
                  )}
                  <div className="position-absolute bottom-0 end-0">
                    <input
                      type="file"
                      id="avatar-upload"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      style={{ display: 'none' }}
                      disabled={uploadingAvatar}
                    />
                    <label
                      htmlFor="avatar-upload"
                      className="btn btn-modern-primary btn-sm rounded-circle"
                      style={{
                        cursor: uploadingAvatar ? 'not-allowed' : 'pointer',
                        width: '45px',
                        height: '45px',
                        boxShadow: '0 4px 15px rgba(144, 202, 249, 0.4)',
                        border: '3px solid #ffffff'
                      }}
                    >
                      {uploadingAvatar ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      ) : (
                        <i className="bx bx-camera" style={{ fontSize: '18px' }}></i>
                      )}
                    </label>
                  </div>
                </div>
                {userData.avatar && userData.avatar.filePath && (
                  <div className="mt-3">
                    <button
                      className="btn btn-modern-danger btn-sm rounded-pill px-3 py-2"
                      onClick={handleAvatarDelete}
                      disabled={uploadingAvatar}
                      style={{
                        fontSize: '12px',
                        fontWeight: '500',
                        boxShadow: '0 2px 8px rgba(255, 205, 210, 0.3)'
                      }}
                    >
                      <i className="bx bx-trash me-1"></i>
                      Fotoğrafı Sil
                    </button>
                  </div>
                )}
              </div>
              <div className="col-md-5">
                <div className="ps-3">
                  <h2 className="mb-2 fw-bold text-dark">{userData.firstName} {userData.lastName}</h2>
                  <p className="text-primary mb-3 fw-medium fs-5">{userData.position}</p>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="d-flex align-items-center p-4 rounded-4" style={{
                        background: 'linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%)',
                        border: '1px solid #e3f2fd',
                        boxShadow: '0 4px 15px rgba(144, 202, 249, 0.1)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{
                          width: '45px',
                          height: '45px',
                          background: 'linear-gradient(135deg, #90caf9 0%, #bbdefb 100%)',
                          boxShadow: '0 4px 12px rgba(144, 202, 249, 0.3)'
                        }}>
                          <i className="bx bx-building text-white" style={{ fontSize: '18px' }}></i>
                        </div>
                        <div>
                          <small className="text-muted d-block fw-medium">Departman</small>
                          <span className="fw-bold text-dark fs-6">{userData.department}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center p-4 rounded-4" style={{
                        background: 'linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%)',
                        border: '1px solid #e3f2fd',
                        boxShadow: '0 4px 15px rgba(144, 202, 249, 0.1)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{
                          width: '45px',
                          height: '45px',
                          background: 'linear-gradient(135deg, #90caf9 0%, #bbdefb 100%)',
                          boxShadow: '0 4px 12px rgba(144, 202, 249, 0.3)'
                        }}>
                          <i className="bx bx-envelope text-white" style={{ fontSize: '18px' }}></i>
                        </div>
                        <div>
                          <small className="text-muted d-block fw-medium">E-posta</small>
                          <span className="fw-bold text-dark fs-6">{userData.email}</span>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center p-4 rounded-4" style={{
                        background: 'linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%)',
                        border: '1px solid #e3f2fd',
                        boxShadow: '0 4px 15px rgba(144, 202, 249, 0.1)',
                        transition: 'all 0.3s ease'
                      }}>
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{
                          width: '45px',
                          height: '45px',
                          background: 'linear-gradient(135deg, #90caf9 0%, #bbdefb 100%)',
                          boxShadow: '0 4px 12px rgba(144, 202, 249, 0.3)'
                        }}>
                          <i className="bx bx-phone text-white" style={{ fontSize: '18px' }}></i>
                        </div>
                        <div>
                          <small className="text-muted d-block fw-medium">Telefon</small>
                          <span className="fw-bold text-dark fs-6">{userData.phone || 'Telefon bilgisi yok'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                {/* Empty space for layout */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Profile Tabs */}
      <div className="row">
        <div className="col-12">
          <div className="modern-card" style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
            border: '1px solid #e3f2fd',
            boxShadow: '0 8px 32px rgba(144, 202, 249, 0.15)'
          }}>
            <div className="p-4 border-bottom" style={{
              background: 'linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%)',
              borderBottom: '2px solid #e3f2fd'
            }}>
              <ul className="nav nav-pills nav-justified">
                <li className="nav-item">
                  <button
                    className={`modern-nav-pill px-4 ${activeTab === 'genel' ? 'active' : ''}`}
                    onClick={() => setActiveTab('genel')}
                  >
                    <i className="bx bx-user me-2"></i>
                    Genel Bilgiler
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`modern-nav-pill px-4 ${activeTab === 'iletisim' ? 'active' : ''}`}
                    onClick={() => setActiveTab('iletisim')}
                  >
                    <i className="bx bx-phone me-2"></i>
                    İletişim
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`modern-nav-pill px-4 ${activeTab === 'is' ? 'active' : ''}`}
                    onClick={() => setActiveTab('is')}
                  >
                    <i className="bx bx-briefcase me-2"></i>
                    İş Bilgileri
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`modern-nav-pill px-4 ${activeTab === 'guvenlik' ? 'active' : ''}`}
                    onClick={() => setActiveTab('guvenlik')}
                  >
                    <i className="bx bx-shield me-2"></i>
                    Güvenlik
                  </button>
                </li>
              </ul>
            </div>

            <div className="p-5">
              {/* Modern Genel Bilgiler Tab */}
              {activeTab === 'genel' && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label modern-form-label fw-bold">Ad</label>
                      <input
                        type="text"
                        className={`form-control rounded-4 border-0 ${editMode ? 'modern-input-edit' : 'modern-input-disabled'}`}
                        name="firstName"
                        value={userData.firstName}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          boxShadow: editMode ? '0 4px 15px rgba(144, 202, 249, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label fw-bold">Soyad</label>
                      <input
                        type="text"
                        className={`form-control rounded-4 border-0 ${editMode ? 'modern-input-edit' : 'modern-input-disabled'}`}
                        name="lastName"
                        value={userData.lastName}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          boxShadow: editMode ? '0 4px 15px rgba(144, 202, 249, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label fw-bold">E-posta</label>
                      <input
                        type="email"
                        className="form-control modern-input-disabled rounded-4"
                        name="email"
                        value={userData.email}
                        disabled
                        style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}
                      />
                      <div className="form-text modern-form-text mt-2">
                        <i className="bx bx-info-circle me-1"></i>
                        E-posta adresi değiştirilemez.
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label modern-form-label fw-bold">Doğum Tarihi</label>
                      {editMode ? (
                        <input
                          type="date"
                          className="form-control modern-input-edit rounded-4"
                          name="birthDate"
                          value={userData.birthDate}
                          onChange={handleInputChange}
                          style={{
                            fontSize: '15px',
                            fontWeight: '500',
                            boxShadow: '0 4px 15px rgba(144, 202, 249, 0.2)'
                          }}
                        />
                      ) : (
                        <input
                          type="text"
                          className="form-control modern-input-disabled rounded-4"
                          value={formatDate(userData.birthDate)}
                          disabled
                          style={{
                            fontSize: '15px',
                            fontWeight: '500',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
                          }}
                        />
                      )}
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label fw-bold">Telefon</label>
                      <input
                        type="tel"
                        className={`form-control rounded-4 ${editMode ? 'modern-input-edit' : 'modern-input-disabled'}`}
                        name="phone"
                        value={userData.phone}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        placeholder="(555) 555 55 55"
                        style={{
                          fontSize: '15px',
                          fontWeight: '500',
                          boxShadow: editMode ? '0 4px 15px rgba(144, 202, 249, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label fw-bold">Adres</label>
                      <textarea
                        className={`form-control rounded-4 ${editMode ? 'modern-input-edit' : 'modern-input-disabled'}`}
                        rows="3"
                        name="address"
                        value={userData.address}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        placeholder="Ev adresinizi girin"
                        style={{
                          resize: 'none',
                          fontSize: '15px',
                          fontWeight: '500',
                          boxShadow: editMode ? '0 4px 15px rgba(144, 202, 249, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                        }}
                      ></textarea>
                    </div>
                  </div>
                </div>
              )}

              {/* Modern İletişim Tab */}
              {activeTab === 'iletisim' && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label modern-form-label">Cep Telefonu</label>
                      <input
                        type="tel"
                        className={`form-control ${editMode ? 'modern-input-edit' : 'modern-input-disabled'}`}
                        name="phone"
                        value={userData.phone}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        placeholder="(555) 555 55 55"
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label">Acil Durum İletişim</label>
                      <input
                        type="text"
                        className={`form-control ${editMode ? 'modern-input-edit' : 'modern-input-disabled'}`}
                        name="emergencyContact"
                        value={userData.emergencyContact}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        placeholder="Ad Soyad - Telefon"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label modern-form-label">İş E-postası</label>
                      <input
                        type="email"
                        className="form-control modern-input-disabled"
                        name="email"
                        value={userData.email}
                        disabled
                      />
                      <div className="form-text modern-form-text">İş e-postası İK tarafından değiştirilebilir.</div>
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label">Ev Adresi</label>
                      <textarea
                        className={`form-control ${editMode ? 'modern-input-edit' : 'modern-input-disabled'}`}
                        rows="4"
                        name="address"
                        value={userData.address}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        placeholder="Ev adresinizi girin"
                        style={{ resize: 'none' }}
                      ></textarea>
                    </div>
                  </div>
                </div>
              )}


              {/* Modern İş Bilgileri Tab */}
              {activeTab === 'is' && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label modern-form-label">Departman</label>
                      <input
                        type="text"
                        className="form-control modern-input-disabled"
                        value={userData.department}
                        disabled
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label">Unvan</label>
                      <input
                        type="text"
                        className="form-control modern-input-disabled"
                        value={userData.position}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-4">
                      <label className="form-label modern-form-label">İşe Başlama Tarihi</label>
                      <input
                        type="text"
                        className="form-control modern-input-disabled"
                        value={formatDate(userData.startDate)}
                        disabled
                      />
                    </div>
                    <div className="mb-4">
                      <label className="form-label modern-form-label">Çalışma Süresi</label>
                      <input
                        type="text"
                        className="form-control modern-input-disabled"
                        value={userData.startDate ? calculateWorkDuration(userData.startDate) : 'Hesaplanamadı'}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="alert modern-alert modern-alert-info">
                      <i className="bx bx-info-circle me-2 text-primary"></i>
                      <strong>Not:</strong> İş bilgileri İnsan Kaynakları departmanı tarafından güncellenir.
                    </div>
                  </div>
                </div>
              )}

              {/* Modern Güvenlik Tab */}
              {activeTab === 'guvenlik' && (
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="bg-white rounded-3 shadow-sm p-4">
                      <h5 className="fw-bold text-dark mb-4">Şifre Değiştir</h5>
                      <form onSubmit={(e) => {
                        e.preventDefault();
                        const formData = new FormData(e.target);
                        const passwordData = {
                          currentPassword: formData.get('currentPassword'),
                          newPassword: formData.get('newPassword'),
                          confirmPassword: formData.get('confirmPassword')
                        };

                        if (passwordData.newPassword !== passwordData.confirmPassword) {
                          showError('Yeni şifreler eşleşmiyor.');
                          return;
                        }

                        if (passwordData.newPassword.length < 6) {
                          showError('Yeni şifre en az 6 karakter olmalıdır.');
                          return;
                        }

                        handlePasswordChange(passwordData);
                      }}>
                        <div className="mb-4">
                          <label className="form-label modern-form-label">Mevcut Şifre</label>
                          <input
                            type="password"
                            className="form-control rounded-3 border-0 bg-light"
                            name="currentPassword"
                            id="currentPassword"
                            required
                            style={{
                              height: '50px',
                              border: editMode ? '2px solid #e3f2fd' : 'none',
                              backgroundColor: editMode ? '#f8f9ff' : '#f8f9fa',
                              boxShadow: editMode ? '0 0 0 2px #e3f2fd' : 'none'
                            }}
                          />
                        </div>
                        <div className="mb-4">
                          <label className="form-label modern-form-label">Yeni Şifre</label>
                          <input
                            type="password"
                            className="form-control rounded-3 border-0 bg-light"
                            name="newPassword"
                            id="newPassword"
                            required
                            minLength="6"
                            style={{
                              height: '50px',
                              border: editMode ? '2px solid #e3f2fd' : 'none',
                              backgroundColor: editMode ? '#f8f9ff' : '#f8f9fa',
                              boxShadow: editMode ? '0 0 0 2px #e3f2fd' : 'none'
                            }}
                          />
                        </div>
                        <div className="mb-4">
                          <label className="form-label modern-form-label">Yeni Şifre (Tekrar)</label>
                          <input
                            type="password"
                            className="form-control rounded-3 border-0 bg-light"
                            name="confirmPassword"
                            id="confirmPassword"
                            required
                            minLength="6"
                            style={{
                              height: '50px',
                              border: editMode ? '2px solid #e3f2fd' : 'none',
                              backgroundColor: editMode ? '#f8f9ff' : '#f8f9fa',
                              boxShadow: editMode ? '0 0 0 2px #e3f2fd' : 'none'
                            }}
                          />
                        </div>
                        <button
                          type="submit"
                          className="btn btn-modern-primary px-4"
                          disabled={saving}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                              Güncelleniyor...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-key me-1"></i>
                              Şifreyi Güncelle
                            </>
                          )}
                        </button>
                      </form>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-white rounded-3 shadow-sm p-4">
                      <h5 className="fw-bold text-dark mb-4">Oturum Geçmişi</h5>
                      <div className="list-group list-group-flush">
                        <div className="list-group-item border-0 bg-light rounded-3 mb-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong className="text-dark">Windows - Chrome</strong>
                              <br />
                              <small className="text-muted">IP: 192.168.1.100</small>
                            </div>
                            <span className="badge bg-success rounded-pill">Aktif</span>
                          </div>
                        </div>
                        <div className="list-group-item border-0 bg-light rounded-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong className="text-dark">iPhone - Safari</strong>
                              <br />
                              <small className="text-muted">IP: 10.0.0.50</small>
                            </div>
                            <span className="badge bg-secondary rounded-pill">2 saat önce</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}


              {/* Action Buttons - Form Altında */}
              <div className="row mt-4">
                <div className="col-12">
                  <div className="d-flex justify-content-center gap-3">
                    {!editMode ? (
                      <button
                        className="btn btn-modern-primary px-5 py-3 d-flex align-items-center gap-2"
                        onClick={() => setEditMode(true)}
                        style={{
                          borderRadius: '25px',
                          fontSize: '16px',
                          fontWeight: '600',
                          boxShadow: '0 6px 20px rgba(144, 202, 249, 0.4)',
                          transition: 'all 0.3s ease',
                          minWidth: '150px',
                          justifyContent: 'center'
                        }}
                      >
                        <i className="bx bx-edit" style={{ fontSize: '20px' }}></i>
                        Düzenle
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn btn-modern-success px-5 py-3 d-flex align-items-center gap-2"
                          onClick={handleSave}
                          disabled={saving}
                          style={{
                            borderRadius: '25px',
                            fontSize: '16px',
                            fontWeight: '600',
                            boxShadow: '0 6px 20px rgba(129, 199, 132, 0.4)',
                            transition: 'all 0.3s ease',
                            minWidth: '150px',
                            justifyContent: 'center'
                          }}
                        >
                          {saving ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status"></span>
                              Kaydediliyor...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-check" style={{ fontSize: '20px' }}></i>
                              Kaydet
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-modern-danger px-5 py-3 d-flex align-items-center gap-2"
                          onClick={() => setEditMode(false)}
                          disabled={saving}
                          style={{
                            borderRadius: '25px',
                            fontSize: '16px',
                            fontWeight: '600',
                            boxShadow: '0 6px 20px rgba(255, 205, 210, 0.4)',
                            transition: 'all 0.3s ease',
                            minWidth: '150px',
                            justifyContent: 'center'
                          }}
                        >
                          <i className="bx bx-x" style={{ fontSize: '20px' }}></i>
                          İptal
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default ProfilePage; 